// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useMemo,
	useState,
} from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// types
import {
	Entity,
	EntityLoan,
	EntityPayload,
	ErrorCause,
	Feedback,
	Form,
	FormField,
	MetadataField,
	MetadataValue,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import {
	Alert,
	AlertTitle,
	Checkbox,
	FormControlLabel,
	Grid,
	SelectChangeEvent,
} from '@mui/material';

// fields
import { DynamicField } from '../../fields/DynamicField/DynamicField';
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface FormEditSupplierProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditSupplier = ({ onClose, onSuccess, setIsLoading }: FormEditSupplierProps) => {
	// hooks
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-supplier';

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [form, setForm] = useState<Form | null>(null);
	const [supplierParentId, setSupplierParentId] = useState<number | null>(null);
	const [searchParams] = useSearchParams();
	const [supplier, setSupplier] = useState<Entity | null>(null);
	const [values, setValues] = useState({
		idExternal: '',
		name: '',
	});

	// params
	const paramDialogSupplier = searchParams.get('dialogSupplier');

	// fetch supplier
	const { isLoading: isLoadingSupplier } = useFetch({
		isEnabled: Boolean(paramDialogSupplier),
		url: `${process.env.REACT_APP_API_URL}/entities/${paramDialogSupplier}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const supplier = res.data[0] as Entity;
				setSupplierParentId((supplier?.loans[0] as EntityLoan)?.parent as number);
				setSupplier(supplier);
				setValues((currentValues) => {
					return {
						...currentValues,
						idExternal: supplier?.idExternal as string,
						name: supplier?.name as string,
					};
				});
			}
		},
	});

	// fetch form
	const { isLoading: isLoadingForm } = useFetch({
		isEnabled: Boolean(supplierParentId && project),
		url: `${process.env.REACT_APP_API_URL}/forms?entity=${supplierParentId}&project=${project?.id}&name=SUPPLIER&limit=1&orderBy=modified&populate=user&status=PUBLISHED`,
		onSuccess: (res) => {
			if (res.data) {
				setForm(res.data[0]);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// define form data
		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setFeedback(null);

			// set loading
			setIsLoading(true);

			if (!project) {
				throw new Error('The project does not exist.', {
					cause: {
						id: 'PROJECT_NON_EXISTENT',
					},
				});
			}

			if (!supplier) {
				throw new Error('The supplier does not exist.', {
					cause: {
						id: 'ENTITY_NON_EXISTENT',
					},
				});
			}

			const body: Partial<EntityPayload> = {
				name: String(entries.name),
			};

			const bodyMetadata = [] as MetadataField[];

			// construct body using fields and entries
			for (let i = 0; i < fields.length; i++) {
				const field = fields[i];

				let value: MetadataValue = entries[field.name] ? String(entries[field.name]) : null;

				if (field.type === 'CHECKBOX') {
					value = Boolean(value);
				}

				if (field.type === 'NUMBER') {
					value = Number(value);
				}

				bodyMetadata.push({
					isProtected: Boolean(field.isProtected),
					label: field.label,
					name: field.name,
					type: field.type,
					value,
				});
			}
			body.metadata = bodyMetadata;

			if (entries.idExternal) {
				body.idExternal = String(entries.idExternal);
			}

			// patch supplier
			const reqPatchSupplier = await fetch(
				`${process.env.REACT_APP_API_URL}/entities/${supplier?.id}`,
				{
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(body),
				}
			);
			const resPatchSupplier = await reqPatchSupplier.json();

			if (resPatchSupplier.error) {
				throw new Error(resPatchSupplier.error.message, {
					cause: {
						id: resPatchSupplier.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.supplierUpdated', {
					name: entries.name,
				}),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'ENTITY_DUPLICATE_ID_EXTERNAL') {
					feedbackNew.alert.children = t('error.supplierDuplicateIdExternal', {
						idExternal: entries.idExternal,
					});
				}

				if (cause.id === 'ENTITY_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set error
			setFeedback(feedbackNew);
		} finally {
			// clear loading
			setIsLoading(false);
		}
	};

	// handlers
	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((currentValues) => {
			return {
				...currentValues,
				[name]: value,
			};
		});
	};

	// define default values for each field
	const fields: FormField[] = useMemo(() => {
		if (form && supplier) {
			return form.fields.map((field) => {
				if (supplier?.metadata) {
					const metadataItem = (supplier.metadata as MetadataField[]).find(
						(f) => f.name === field.name
					);
					if (metadataItem) {
						if (field.type === 'CHECKBOX') {
							field.defaultChecked =
								metadataItem.value === undefined ? false : Boolean(metadataItem.value);
						} else {
							field.defaultValue = metadataItem.value;
						}
					}
				}
				return {
					...field,
				};
			});
		}
		return [];
	}, [form, supplier]);

	const renderContent = () => {
		if (isLoadingForm || isLoadingSupplier) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				{fields.map((field, i) => {
					return (
						<Grid
							alignSelf={field.type === 'CHECKBOX' ? 'flex-end' : 'flex-start'}
							item
							key={`field-${field.name}-${i}`}
							lg={6}
							xs={12}>
							<DynamicField field={field} />
						</Grid>
					);
				})}
			</>
		);
	};

	return (
		<form id={idForm} name="formEditSupplier" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						helperText={t('idExternal.helper')}
						id={`${idForm}-id-external`}
						label={t('uniqueIdentifier')}
						name="idExternal"
						onChange={handleChangeValue}
						value={values.idExternal}
					/>
				</Grid>
				{renderContent()}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox
								checked={true}
								disabled={Boolean(supplier)}
								id={`${idForm}-is-kyc-verified`}
								name="isKycVerified"
								required={true}
							/>
						}
						label={t('formAddEntity.kycAml')}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
